/**
 * Períodos de relatório em dia-calendário de Brasília.
 *
 * O banco guarda epoch em UTC. Agrupar por `date(ts,'unixepoch')` joga uma
 * mensagem das 22h de Brasília no dia seguinte — com "Hoje" e "Ontem" na tela,
 * isso deixa de ser um detalhe e vira número errado.
 *
 * O Brasil não usa mais horário de verão desde 2019, então America/Sao_Paulo é
 * UTC-03:00 fixo e dá para resolver com deslocamento constante, sem carregar
 * base de fusos para dentro do Worker. Se isso voltar a mudar, é aqui que
 * muda — e só aqui.
 */
export const BRT_OFFSET_MINUTES = -180

/** Modificador de fuso aceito pelas funções de data do SQLite. */
export const BRT_SQLITE_MODIFIER = '-03:00'

const MS_POR_DIA = 86_400_000
const OFFSET_MS = BRT_OFFSET_MINUTES * 60_000

export const PERIOD_KEYS = ['hoje', 'ontem', '7d', '30d', '90d'] as const
export type PeriodKey = (typeof PERIOD_KEYS)[number]

export interface ResolvedPeriod {
  key: PeriodKey
  /** Início do intervalo, inclusivo. */
  from: Date
  /** Fim do intervalo, exclusivo. */
  to: Date
  /** Dias-calendário cobertos, para o gráfico saber quantas colunas desenhar. */
  dias: number
}

/** Meia-noite de Brasília do dia em que `instante` cai, em UTC. */
export function startOfDayBrt(instante: Date | number): Date {
  const ms = typeof instante === 'number' ? instante : instante.getTime()
  // Desloca para "hora local", trunca o dia, desloca de volta.
  const local = ms + OFFSET_MS
  return new Date(local - (local % MS_POR_DIA) - OFFSET_MS)
}

/** `YYYY-MM-DD` do dia de Brasília — a mesma chave que o SQL produz. */
export function dayKeyBrt(instante: Date | number): string {
  const ms = typeof instante === 'number' ? instante : instante.getTime()
  return new Date(ms + OFFSET_MS).toISOString().slice(0, 10)
}

/**
 * Converte a escolha da interface em intervalo concreto.
 *
 * "Hoje" vai até agora, não até a meia-noite futura: o gráfico não desenha
 * horas que ainda não aconteceram.
 */
export function resolvePeriod(key: PeriodKey, agora: Date = new Date()): ResolvedPeriod {
  const inicioHoje = startOfDayBrt(agora)

  if (key === 'hoje') {
    return { key, from: inicioHoje, to: agora, dias: 1 }
  }

  if (key === 'ontem') {
    const inicioOntem = new Date(inicioHoje.getTime() - MS_POR_DIA)
    return { key, from: inicioOntem, to: inicioHoje, dias: 1 }
  }

  const dias = key === '7d' ? 7 : key === '30d' ? 30 : 90
  return {
    key,
    from: new Date(inicioHoje.getTime() - (dias - 1) * MS_POR_DIA),
    to: agora,
    dias,
  }
}

/** Dias-calendário de Brasília do intervalo, do mais antigo para o mais novo. */
export function eachDayBrt(period: ResolvedPeriod): string[] {
  const dias: string[] = []
  for (let i = 0; i < period.dias; i++) {
    dias.push(dayKeyBrt(period.from.getTime() + i * MS_POR_DIA))
  }
  return dias
}
